import React from 'react';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import Arrows from '../illustrations/Arrows';

// Simple illustration components for the cards - CONSISTENT SIZES
const OneAtATimeIcon = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="11" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M16 24 L22 30 L32 18" stroke="#00FFC6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="24" cy="24" r="1" fill="#00FFC6" opacity="0.2"/>
  </svg>
);

const FeelsAliveIcon = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none">
    <path d="M14 24 C14 18 18 14 24 14 C30 14 34 18 34 24 C34 30 30 34 24 34 C18 34 14 30 14 24Z" 
          stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M20 20 C22 18 26 18 28 20" stroke="#00FFC6" strokeWidth="2" strokeLinecap="round"/>
    <path d="M20 28 C22 26 26 26 28 28" stroke="#00FFC6" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="20" cy="22" r="1.5" fill="#00FFC6"/>
    <circle cx="28" cy="22" r="1.5" fill="#00FFC6"/>
  </svg>
);

const SmartLogicIcon = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none">
    <path d="M18 18 L30 18 L30 30 L18 30 Z" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M22 22 L26 22" stroke="#00FFC6" strokeWidth="2" strokeLinecap="round"/>
    <path d="M22 26 L26 26" stroke="#00FFC6" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="20" cy="14" r="1.5" fill="#00FFC6"/>
    <circle cx="28" cy="14" r="1.5" fill="#00FFC6"/>
    <path d="M14 34 L34 34" stroke="currentColor" strokeWidth="2" strokeDasharray="2,2"/>
    <circle cx="24" cy="38" r="1.5" fill="#00FFC6"/>
  </svg>
);

const FormsReimagined = () => {
  const [ref, isVisible] = useScrollAnimation();

  const cards = [
    {
      title: 'One at a time',
      desc: 'Focus on what matters. No overwhelming forms.',
      icon: OneAtATimeIcon,
    },
    {
      title: 'Feels alive',
      desc: 'Every interaction has weight and meaning.',
      icon: FeelsAliveIcon,
    },
    {
      title: 'Smart logic',
      desc: 'Adapts to responses. Intelligent branching.',
      icon: SmartLogicIcon,
    },
  ];

  return (
    <section
      ref={ref}
      className="py-24 px-6 bg-white relative overflow-hidden"
    >
      <Arrows className="absolute bottom-20 left-10 text-gray-400 opacity-30 hidden md:block" />

      <div
        className={`max-w-6xl mx-auto transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        {/* Top text grid - FIXED ALIGNMENT */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-start">
          {/* Left column - always left aligned */}
          <div className="text-left">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight text-black">
              Forms.<br />Reimagined.
            </h2>
          </div>
          
          {/* Right column - left aligned on all screens */}
          <div className="text-left">
            <p className="text-lg sm:text-xl leading-relaxed text-gray-700">
              Fylo isn't another form builder.
              <br />
              <br />
              It's a new language for interaction, where answering feels like
              chatting, selecting feels like choosing, and submitting feels like
              completing a story.
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-16 md:mt-20">
          {cards.map((card, i) => (
            <div
              key={i}
              className="p-6 md:p-8 bg-gray-50 rounded-2xl hover:scale-105 transition-transform duration-500 group"
              style={{
                transitionDelay: `${i * 100}ms`,
              }}
            >
              {/* Illustration Container - Consistent sizing */}
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <card.icon className="text-black group-hover:text-blue-600 transition-colors duration-300 w-full h-full" />
                </div>
                {/* Title next to the icon on ALL screens */}
                <h3 className="text-xl md:text-2xl font-bold ml-4 text-black">
                  {card.title}
                </h3>
              </div>

              {/* Description */}
              <p className="text-gray-600 text-sm md:text-base mt-4">{card.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FormsReimagined;